<template>
    <div v-if="isLoading" class="fixed top-0 left-0 z-50 h-1 w-full">
        <div
            :style="{
                width: (progress || 0) * 100 + '%',
            }"
            class="h-full bg-indigo-600 transition-[width] duration-200 ease-out"
        />
    </div>
    <!--
        The subscription bar sits above every shell — main, POS, auth and blank
        alike — because a lapsed subscription is true on every page. It renders
        in flow and the sticky topbar and breadcrumbs read its height off
        --app-banner-offset, which the component sets through a class on <html>.
    -->
    <BillingNotice></BillingNotice>
    <OfflineNotice></OfflineNotice>

    <!--
        The chrome is mounted here, above RouterView, so a navigation swaps only
        the page inside it. Sidebar, topbar and their state survive the move;
        previously every page carried its own layout, so each navigation tore the
        whole shell down and built a new one.
    -->
    <component :is="layoutComponent" v-if="layoutComponent">
        <RouterView v-slot="{ Component, route }">
            <!-- Keyed by route name so the entry animation replays per page, while
                 a param change (branch 1 -> 2) still reuses the instance as before. -->
            <div :key="route.name" class="page-enter">
                <component :is="Component" />
            </div>
        </RouterView>
    </component>

    <Toast></Toast>
</template>

<script lang="js" setup>
import BillingNotice from '@/components/BillingNotice.vue';
import OfflineNotice from '@/components/OfflineNotice.vue';
import Toast from '@/components/Toast.vue';
import Main from '@/layouts/Main.vue';
import SalesMain from '@/layouts/SalesLayout/Main.vue';
import Blank from '@/layouts/Blank.vue';
import Public from '@/layouts/Public.vue';
import Authentication from '@/layouts/Authentication.vue';
import { computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth-store.js';
import { isLoading, progress } from '@/composables/useRouteProgress';
import { useNotifications } from '@/composables/useNotifications.js';
import { useConfigurationStore } from '@/stores/configuration-store.js';
import { leaveOfflineSync, subscribeOfflineSync } from '@/services/offlineRealtimeService.js';
import { onOfflineToOnline } from '@/services/onlineCheckService.js';
import { isOfflineFlagEnabled } from '@/configs/offlineFeatureFlags.js';
import { convergeOfflineCaches } from '@/services/offlineConvergenceService.js';

const configStore = useConfigurationStore();

configStore.loadFromCache();

// Which shell a route wants. `sales` is the POS chrome (its own topbar); the rest
// map straight to the layout of the same name.
const LAYOUTS = { main: Main, sales: SalesMain, blank: Blank, public: Public, auth: Authentication };

const router = useRouter();
const layoutComponent = computed(() => LAYOUTS[router.currentRoute.value.meta?.layout] ?? null);

const auth = useAuthStore();
const { fetchNotifications, listenForNotifications, stopListening, notifications, unreadCount } = useNotifications();

let stopOnlineTransition = null;
let initializedOnlineUserId = null;

const isCurrentOnlineUser = (userId) =>
    Boolean(
        userId &&
            auth.getOnlineStatus &&
            auth.isLoggedIn &&
            String(auth.user?.id ?? '') === String(userId),
    );

const leaveUserChannels = () => {
    if (initializedOnlineUserId != null) {
        stopListening(initializedOnlineUserId);
    }
    leaveOfflineSync();
    initializedOnlineUserId = null;
    notifications.value = [];
    unreadCount.value = 0;
};

const initializeOnlineUser = async (userId, syncReason) => {
    if (!isCurrentOnlineUser(userId)) return;

    await configStore.fetchConfigurations();
    if (!isCurrentOnlineUser(userId)) return;
    await configStore.fetchAddonFeatures();
    if (!isCurrentOnlineUser(userId)) return;
    // The subscription's standing, so the bar and the write gate start from
    // the server's answer rather than the cached one. Fails quietly: a 402 or
    // a billing notice will bring it current later, and the cache stands
    // until then.
    await configStore.fetchBillingState();
    if (!isCurrentOnlineUser(userId)) return;
    while (auth.isUpdatingCSRF) {
        await new Promise((r) => setTimeout(r, 500));
        if (!isCurrentOnlineUser(userId)) return;
    }
    if (!isCurrentOnlineUser(userId)) return;

    await fetchNotifications();
    // fetchNotifications intentionally handles its own request error. A 401
    // can still complete logout through the Axios interceptor, so validate the
    // session again before subscribing the old identity to private channels.
    if (!isCurrentOnlineUser(userId)) return;
    if (initializedOnlineUserId !== String(userId)) {
        listenForNotifications(userId);
        if (!isCurrentOnlineUser(userId)) {
            stopListening(userId);
            return;
        }
        subscribeOfflineSync(userId);
        initializedOnlineUserId = String(userId);
    }

    if (isOfflineFlagEnabled('listStoresSync') && isCurrentOnlineUser(userId)) {
        await convergeOfflineCaches({ reason: syncReason });
    }
};

watch(
    () => auth.isLoggedIn && auth.user?.id,
    async (loggedInWithId, wasLoggedIn) => {
        if (loggedInWithId) {
            if (initializedOnlineUserId != null && initializedOnlineUserId !== String(loggedInWithId)) {
                leaveUserChannels();
            }
            initializeOnlineUser(loggedInWithId, 'login').catch((e) => console.warn('[offlineSync] login initialization', e));
        } else if (wasLoggedIn) {
            leaveUserChannels();
        }
    },
    { immediate: true },
);

/**
 * Idle tabs miss Echo; refresh list caches when the user returns.
 *
 * The sync is incremental, so a resume costs the rows that changed rather than
 * the tables. The interval is still generous because alt-tabbing is not evidence
 * that anything moved — Echo already covers a tab that stayed connected, and
 * this is the repair path for one that did not.
 */
const VISIBILITY_LIST_SYNC_MIN_MS = 10 * 60_000;
let lastVisibilityListSyncAt = 0;
let visibilitySyncInFlight = null;

const onVisibilityResume = () => {
    if (typeof document !== 'undefined' && document.hidden) return;
    const userId = auth.user?.id;
    if (!isCurrentOnlineUser(userId)) return;
    if (!isOfflineFlagEnabled('listStoresSync')) return;

    const now = Date.now();
    if (now - lastVisibilityListSyncAt < VISIBILITY_LIST_SYNC_MIN_MS) return;
    lastVisibilityListSyncAt = now;

    if (visibilitySyncInFlight) return visibilitySyncInFlight;
    visibilitySyncInFlight = convergeOfflineCaches({ reason: 'visibility_resume' })
        .catch((e) => console.warn('[offlineSync] visibility resume', e))
        .finally(() => {
            visibilitySyncInFlight = null;
        });
    return visibilitySyncInFlight;
};

onMounted(async () => {
    stopOnlineTransition = onOfflineToOnline(() => {
        if (!auth.isLoggedIn) return;
        return auth.FETCH_USER_DATA(true).then(() => initializeOnlineUser(auth.user?.id, 'offline_to_online'));
    });
    document.addEventListener('visibilitychange', onVisibilityResume);
});

onUnmounted(() => {
    leaveUserChannels();
    if (typeof stopOnlineTransition === 'function') stopOnlineTransition();
    document.removeEventListener('visibilitychange', onVisibilityResume);
});

if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
        .then((registration) => {
            registration.active?.postMessage({ type: 'CLAIM_CLIENTS' });
        })
        .catch((error) => console.warn('[serviceWorker] readiness failed', error));
}
</script>

<style scoped></style>
